import { memo, useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { AccountData } from "@markit/common.types";
import {
  censoredWordsDetected,
  detectedCensored,
  PhoneErrorType,
} from "@markit/common.utils";
import { Colors } from "../../utils/colors";
import CustomTextField from "../../components/CustomTextField";
import CustomPhoneTextField from "../../components/TextFields/CustomPhoneTextField";
import RectangleButton from "../../components/Buttons/RectangleButton";
import LoginWelcomeBody from "../../components/Login/LoginWelcomeBody";
import { showPhoneInputError } from "../../utils/phoneUtils";
import { WeeklyUpdatesDisclosure } from "../../components/Login/WeeklyUpdatesDisclosure";
import { useTheme } from "../../hooks/useTheme";

type LoginOnboardingAccountDetailsProps = {
  accountSettings: AccountData;
  updateAccountSettings: (accountSettings: Partial<AccountData>) => void;
  loading: boolean;
  onFinishSetUp: (phoneNumber: string) => Promise<void>;
};

// Shown before Profile Setup only for accounts that skipped LoginWelcome's
// sign-up form (currently just Google sign up) - that form is where fullName
// + phone number normally get collected, along with weekly-updates opt-in.
const LoginOnboardingAccountDetails = (
  props: LoginOnboardingAccountDetailsProps
) => {
  const { accountSettings, updateAccountSettings, loading, onFinishSetUp } =
    props;
  const { theme } = useTheme();
  const [fullNameError, setFullNameError] = useState({
    status: false,
    message: "",
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState<PhoneErrorType>({
    isError: false,
    message: "",
  });

  const renderInputIcon = useCallback(
    (name: string, isDefined: boolean, isError?: boolean) => (
      <Icon
        icon={name}
        height={22}
        style={{
          color: isError
            ? Colors.RED3
            : isDefined
              ? Colors.BLACK
              : Colors.GRAY2,
          marginLeft: 14,
        }}
      />
    ),
    []
  );

  const continueOnPress = useCallback(async () => {
    const trimmedFullName = accountSettings.fullName.trim();
    if (!trimmedFullName) {
      setFullNameError({ status: true, message: "Full Name cannot be empty." });
      return;
    }
    if (detectedCensored(trimmedFullName)) {
      setFullNameError({
        status: true,
        message: `Content violation: banned words detected (${censoredWordsDetected(
          trimmedFullName
        )})`,
      });
      return;
    }
    if (showPhoneInputError(phoneNumber, setPhoneNumberError)) {
      return;
    }
    await onFinishSetUp(phoneNumber);
  }, [accountSettings.fullName, phoneNumber, onFinishSetUp]);

  return (
    <LoginWelcomeBody
      title="Welcome to Markit"
      subtext="Add your account name and phone number"
    >
      <div className="ColumnNormal" style={{ gap: 14, width: "100%" }}>
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <span className="bodySmallMedium">Account Name</span>
          <CustomTextField
            value={accountSettings.fullName}
            placeholder="What do you want to go by?"
            inputMode="text"
            borderRadius={12}
            onChange={(change: any) => {
              setFullNameError({ status: false, message: "" });
              updateAccountSettings({ fullName: change.target.value });
            }}
            error={fullNameError.status}
            altPadding={"8px 7px"}
            startAdornment={renderInputIcon(
              "ion:person",
              !!accountSettings.fullName,
              fullNameError.status
            )}
          />
          {fullNameError.status ? (
            <span
              style={{
                fontSize: 12,
                color: theme.ErrorBorderColor.borderColor,
              }}
            >
              {fullNameError.message}
            </span>
          ) : null}
        </div>
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <span className="bodySmallMedium">Phone Number</span>
          <CustomPhoneTextField
            placeholder="Enter phone number"
            phoneNumber={phoneNumber}
            onPhoneChange={(value) => {
              setPhoneNumberError({ isError: false, message: "" });
              setPhoneNumber((value as string) || "");
            }}
            showPhoneError={phoneNumberError}
            startAdornment={
              <Icon
                icon="mdi:phone"
                height={22}
                style={{
                  color: phoneNumberError.isError
                    ? Colors.RED3
                    : phoneNumber
                      ? Colors.BLACK
                      : Colors.GRAY2,
                  marginBottom: -3,
                }}
              />
            }
            autoCompleteOn
          />
        </div>
        <RectangleButton
          buttonLabel={<span>Continue</span>}
          onPress={continueOnPress}
          theme={theme}
          altPaddingVert={14}
          disabled={loading}
          loading={loading}
        />
        <WeeklyUpdatesDisclosure />
      </div>
    </LoginWelcomeBody>
  );
};

export default memo(LoginOnboardingAccountDetails);
